import { AttributeType } from "@/geoMap/define/AttributeType";
import { CqlParameter } from "@/geoMap/net/CqlParameter";
import { Feature, Map } from "ol";
import { FeatureGeoJson } from "../FeatureGeoJson";
import GeoJSON from "ol/format/GeoJSON";
import { ILayerFactory } from "./ILayerFactory";
import ImageLayer from "ol/layer/Image";
import { ImageWMS } from "ol/source";
import { MapContextMenuItem } from "../MapContextMenuItem";
import { SelectedFeatureInfo } from "../SelectedFeatureInfo";
import { WMSGetFeatureManager } from "../net/WMSGetFeatureManager";
import { ODSumDeparturesArrivalsLayerName } from "./ODSumDeparturesArrivalsLayerName";
import { IAttributeConvert } from "@/attribute/convert/IAttributeConvert";
import { ODNodeOccurrenceAttributeConvert } from "@/attribute/convert/ODNodeOccurrenceAttributeConvert";
import { ODNodeConcentrationAttributeConvert } from "@/attribute/convert/ODNodeConcentrationAttributeConvert";
import { ODNodeOccurrenceAndConcentrationAttributeConvert } from "@/attribute/convert/ODNodeOccurrenceAndConcentrationAttributeConvert";
import { ODDataType } from "./IODLayerName";
import { WFSGetFeatureManager } from "../net/WFSGetFeatureManager";
import { Circle, Stroke, Style } from "ol/style";
import ImageWMSEx from "@/geoMap/net/ImageWMSEx";
import { LayerUtil } from "../util/LayerUtil"

/**
 * 最大ズームレベル
 */
const LAYER_MAX_ZOOMLEVEL: Record<ODDataType, number> = {
  Bゾーン: 18,
  市区町村: 18,
  都道府県: 18,
};

/**
 * 最小ズームレベル
 */
const LAYER_MIN_ZOOMLEVEL: Record<ODDataType, number> = {
  Bゾーン: 12,
  市区町村: 10,
  都道府県: 6,
};

/**
 * API通信用URL
 */
const URL_API = process.env.VUE_APP_XROAD_API_URL;

type AttributeConvertInfo = {
  contextMenu: string;
  attributeConvert: IAttributeConvert;
};

/**
 * OD発集量属性変換クラス取得関数
 */
const getAttributeConvertInfo = (
  feature: Feature,
  layerName: ODSumDeparturesArrivalsLayerName
): AttributeConvertInfo => {
  const attributeConvertInfo: AttributeConvertInfo = {
    contextMenu: `OD_発生・集中量_${layerName.odDataType}`,
    attributeConvert: null,
  };

  switch (layerName.btzType) {
    case "発生量":
      attributeConvertInfo.attributeConvert =
        new ODNodeOccurrenceAttributeConvert(
          feature,
          layerName.odDataType,
          layerName.carType,
          layerName.yearType
        );
      break;
    case "集中量":
      attributeConvertInfo.attributeConvert =
        new ODNodeConcentrationAttributeConvert(
          feature,
          layerName.odDataType,
          layerName.carType,
          layerName.yearType
        );
      break;
    case "発生集中量":
      attributeConvertInfo.attributeConvert =
        new ODNodeOccurrenceAndConcentrationAttributeConvert(
          feature,
          layerName.odDataType,
          layerName.carType,
          layerName.yearType
        );
      break;
    default:
      throw new Error("Invalid value given for parameter layerName.btzType.");
  }

  return attributeConvertInfo;
};

/**
 * 市区町村区分で選択された地物の交通量に対する直径を求めるための条件
 * 交通量が[idx][0]以上なら、半径は[idx][1]
 */
const TRAFFIC_TO_DIAMETER_STEPS = {
  市区町村: [
    [0, 10],
    [45000, 20],
    [90000, 30],
    [135000, 40],
    [180000, 50],
    [225000, 60],
    [270000, 70],
    [315000, 80],
    [360000, 90],
    [400000, 100],
  ],
  都道府県: [
    [0, 10],
    [200000, 20],
    [400000, 30],
    [600000, 40],
    [800000, 50],
    [1000000, 60],
    [1200000, 70],
    [1400000, 80],
    [1600000, 90],
    [1800000, 100],
  ],
};

/**
 * 描画する円の半径を求める関数
 * @param odDataType データ種別（Bゾーン以外）
 * @param trafficValue 交通量
 * @returns 描画する円の半径
 */
const getSelectedFeatureCircleRadius = (
  odDataType: Exclude<ODDataType, "Bゾーン">,
  trafficValue: number
): number => {
  if (trafficValue < 0) return 0;

  const steps = TRAFFIC_TO_DIAMETER_STEPS[odDataType];

  // 交通量がどの条件値より小さいか
  let index = steps.findIndex((arr) => trafficValue < arr[0]);

  // どの条件値よりも大きかった場合
  if (index == -1) index = steps.length;

  const radius = steps[index - 1][1] / 2;

  return radius;
};

/**
 * OD発集量レイヤのクラス
 */
export class ODSumDeparturesLayerFactory implements ILayerFactory {
  /**
   * 表示WMSレイヤ
   */
  private _wmsLayer: ImageLayer<ImageWMS>;

  /**
   * 表示WMSソース
   */
  private _wmsImageSource: ImageWMS;

  /**
   * 表示対象ETCレイヤ
   */
  private _visibleLayer: ODSumDeparturesArrivalsLayerName;

  /**
   * 地物属性の有無を取得
   */
  isFeatureInfo(): boolean {
    return true;
  }

  /**
   * 範囲地物属性の有無を取得
   */
  isRangeFeatureInfo(): boolean {
    return true;
  }

  /**
   * 右クリック処理の有無を取得
   */
  isRightClickInfo(): boolean {
    return false;
  }

  /**
   * レイヤ定義を取得
   * @param _map openLayers Mapクラス
   */
  getViewLayer(_map?: Map): ImageLayer<ImageWMS>[] {
    //WMSレイヤの取得
    this._wmsImageSource = new ImageWMSEx({
      url: URL_API,
      params: {
        LAYERS: "",
        EXCEPTIONS: "application/json",
      },
      ratio: 1,
      serverType: "geoserver",
      crossOrigin: "Anonymous",
    });
    this._wmsLayer = new ImageLayer({
      source: this._wmsImageSource,
    });

    return [this._wmsLayer];
  }

  /**
   * 地物情報を取得
   * @param _map 地図
   * @param _latitude クリック座標緯度
   * @param _longitude クリック座標経度
   * @param x クリック位置X
   * @param y クリック位置Y
   * @param width 地図幅
   * @param height 地図高さ
   * @param bBox 地図範囲（BBOX)
   */
  async getFeatureInfo(
    _map: Map,
    _latitude: number,
    _longitude: number,
    x: number,
    y: number,
    width: number,
    height: number,
    bBox: number[]
  ): Promise<SelectedFeatureInfo[]> {
    const zoom = _map.getView().getZoom();
    const minZoom = LAYER_MIN_ZOOMLEVEL[this._visibleLayer.odDataType];
    const maxZoom = LAYER_MAX_ZOOMLEVEL[this._visibleLayer.odDataType];
    if (zoom < minZoom || maxZoom < zoom) {
      return null;
    }

    // cqlパラメータクラスを作成
    const layerName = this._visibleLayer.getLayerName();
    const cqlFilterString = this._visibleLayer.getCqlFilterString();
    const cqlParameters = [new CqlParameter(layerName, cqlFilterString)];

    const WMSGetter = new WMSGetFeatureManager();
    const selectedFeatureGeoJSON = await WMSGetter.getInfo(
      URL_API,
      bBox.join(","),
      cqlParameters,
      x,
      y,
      height,
      width
    ).catch((error: any) => {
      throw error;
    });
    const selectedFeatures = new GeoJSON().readFeatures(selectedFeatureGeoJSON);
    if (selectedFeatures.length === 0) return null;
    const selectFeatureInfoList: SelectedFeatureInfo[] = await Promise.all(
      selectedFeatures.map(async (feature) => {
        const id = feature.getId().toString();
        const layerKey = id.split(".")[0];

        const WFSGetter = new WFSGetFeatureManager();
        const wfsGeoJson = await WFSGetter.getInfo(
          URL_API,
          cqlParameters,
          null,
          feature.getId()
        ).catch((error) => {
          throw error;
        });
        const wfsFeature = new GeoJSON().readFeatures(wfsGeoJson)[0];
        if (!wfsFeature) return;

        //属性変換クラス・attributeクラスを生成
        let attributeConvertInfo: AttributeConvertInfo = null;
        let attributeType: AttributeType = null;

        attributeConvertInfo = getAttributeConvertInfo(
          wfsFeature,
          this._visibleLayer
        );

        attributeType = AttributeType.Normal;

        // selectFeatureを生成（地物情報のみ）
        const geometry = wfsFeature.getGeometry();
        const selectFeature = new Feature({
          geometry,
        });

        // 選択地物の描画スタイルを設定
        const odDataType = this._visibleLayer.odDataType;
        if (odDataType == "Bゾーン") {
          selectFeature.setStyle(
            new Style({
              stroke: new Stroke({ width: 3, color: "hotpink" }),
            })
          );
        } else {
          const traffic = feature.getProperties()["交通量"];
          const radius = getSelectedFeatureCircleRadius(odDataType, traffic);
          selectFeature.setStyle(
            new Style({
              image: new Circle({
                stroke: new Stroke({ width: 3, color: "hotpink" }),
                radius: radius,
              }),
            })
          );
        }

        // featureGeoJSONを生成
        const geoJSON = new GeoJSON().writeFeature(wfsFeature);
        const featureGeoJSON = new FeatureGeoJson(geoJSON, id);
        // selectedFeatureInfoを生成して、リストに追加
        const selectedFeatureInfo = new SelectedFeatureInfo(
          selectFeature,
          attributeConvertInfo.contextMenu,
          featureGeoJSON,
          attributeType,
          attributeConvertInfo.attributeConvert,
          false,
          layerKey,
          "",
          id
        );
        return selectedFeatureInfo;
      })
    );
    return selectFeatureInfoList;
  }

  /**
   * 右クリック処理
   * @param map 地図
   * @param latitude クリック座標緯度
   * @param longitude クリック座標経度
   * @param x クリック位置X
   * @param y クリック位置Y
   * @param width 地図幅
   * @param height 地図高さ
   * @param bBox 地図範囲（BBOX)
   */
  async getRightClickInfo(
    _map: Map,
    _latitude: number,
    _longitude: number,
    _x: number,
    _y: number,
    _width: number,
    _height: number,
    _bBox: number[]
  ): Promise<MapContextMenuItem[]> {
    return null;
  }

  /**
   * 指定範囲の地物を取得
   * @param _map 地図
   * @param bBox 地図範囲（BBOX)
   */
  async getRangeFeatureInfo(
    _map: Map,
    bBox: number[]
  ): Promise<SelectedFeatureInfo[]> {
    const zoom = _map.getView().getZoom();
    const minZoom = LAYER_MIN_ZOOMLEVEL[this._visibleLayer.odDataType];
    const maxZoom = LAYER_MAX_ZOOMLEVEL[this._visibleLayer.odDataType];
    if (zoom < minZoom || maxZoom < zoom) {
      return null;
    }
    // cqlパラメータクラスを作成
    const layerName = this._visibleLayer.getLayerName();
    const cqlFilterString = this._visibleLayer.getCqlFilterString();
    const cqlParameters = [new CqlParameter(layerName, cqlFilterString)];

    const WFSGetter = new WFSGetFeatureManager();
    const selectedFeatureGeoJSON = await WFSGetter.getInfo(
      URL_API,
      cqlParameters,
      bBox
    ).catch((error) => {
      throw error;
    });
    const selectedFeatures = new GeoJSON().readFeatures(selectedFeatureGeoJSON);
    if (selectedFeatures.length === 0) return null;
    const selectFeatureInfoList: SelectedFeatureInfo[] = [];
    selectedFeatures.forEach((feature) => {
      const id = feature.getId().toString();

      //属性変換クラス・attributeクラスを生成
      let attributeConvertInfo: AttributeConvertInfo = null;
      let attributeType: AttributeType = null;

      attributeConvertInfo = getAttributeConvertInfo(
        feature,
        this._visibleLayer
      );

      attributeType = AttributeType.Normal;

      // selectFeatureを生成（地物情報のみ）
      const geometry = feature.getGeometry();
      const selectFeature = new Feature({
        geometry,
      });

      // featureGeoJSONを生成
      const geoJSON = new GeoJSON().writeFeature(feature);
      const featureGeoJSON = new FeatureGeoJson(geoJSON, id);
      // selectedFeatureInfoを生成して、リストに追加
      const selectedFeatureInfo = new SelectedFeatureInfo(
        selectFeature,
        attributeConvertInfo.contextMenu,
        featureGeoJSON,
        attributeType,
        attributeConvertInfo.attributeConvert
      );
      selectFeatureInfoList.push(selectedFeatureInfo);
    });
    return selectFeatureInfoList;
  }

  /**
   * 表示状態のレイヤ数を返す
   */
  getVisibleLayerCount(): number {
    return this._wmsLayer?.getVisible() ? 1 : 0;
  }

  /**
   * レイヤの表示状態を設定
   * @param isVisible 表示有無
   * @param visibleLayer 対象レイヤ
   */
  setVisible(
    isVisible: boolean,
    visibleLayer: ODSumDeparturesArrivalsLayerName
  ) {
    if (isVisible) {
      if (visibleLayer) {
        this._visibleLayer = visibleLayer;
      }
      this._wmsImageSource.updateParams({
        LAYERS: this._visibleLayer.getLayerName(),
        CQL_FILTER: this._visibleLayer.getCqlFilterString(),
      });
      this._wmsLayer.setMaxZoom(
        LAYER_MAX_ZOOMLEVEL[this._visibleLayer.odDataType]
      );
      // ズームレベルがLAYER_MIN_ZOOMLEVELに等しいときは表示されるようにしたいため、-1する
      this._wmsLayer.setMinZoom(
        LAYER_MIN_ZOOMLEVEL[this._visibleLayer.odDataType] - 1
      );
      this._wmsImageSource.refresh();
      this._wmsLayer.setVisible(true);
    } else {
      this._wmsLayer.setVisible(false);
    }
  }
  /**
   * 表示レイヤが存在するか判定
   */
   isVisibleLayer(_map:Map) {
    return LayerUtil.isVisibleLayer(_map, this._wmsLayer)
  }
}
